import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'semantic-ui-react';

/**
 * Builds a listing-block template that renders one card per result.
 *
 * Nine of these views (Competition, Course, Event, ExternalProject,
 * Project, Robot, Talk, Topic, Video) were byte-identical apart from the
 * card they imported - after stripping the type name they hashed the same.
 * The remaining ones differ only in their container class or in an outer
 * semantic-ui Container, both of which are options here.
 *
 * @param CardComponent  card to render per item; receives
 *                       `item`, `compact`, `hideLink`, `gridLayout`
 * @param displayName    component name, for React DevTools and warnings
 * @param containerClass extra class on `.cards__container` (People and
 *                       News bring their own fixed grid)
 * @param useContainer   wrap in semantic-ui's `<Container>` (Jobs, Theses)
 * @param supportsGrid   whether the `grid_layout` setting applies; false
 *                       for the listings that are already a grid
 * @param hideLinkByDefault  what an unset `hide_link` means. News cards never
 *                       linked to their full view, so leaving that off by
 *                       default keeps every existing block rendering exactly
 *                       as it did while making the link available as an
 *                       option.
 */
const createCardListingView = (
  CardComponent,
  {
    displayName,
    containerClass = '',
    useContainer = false,
    supportsGrid = true,
    hideLinkByDefault = false,
  } = {},
) => {
  const CardListingView = ({ items, compact, hide_link, grid_layout }) => {
    const useGrid = supportsGrid && grid_layout;
    const classes = [
      'cards__container',
      containerClass,
      useGrid ? 'cards__container--grid' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const cards = (
      <div className={classes}>
        {items &&
          items.map((item) => (
            <CardComponent
              key={item['@id']}
              item={item}
              compact={compact}
              hideLink={hide_link ?? hideLinkByDefault}
              gridLayout={useGrid}
            />
          ))}
      </div>
    );

    return useContainer ? (
      <Container className="view-wrapper">{cards}</Container>
    ) : (
      <div className="view-wrapper">{cards}</div>
    );
  };

  CardListingView.displayName = displayName || 'CardListingView';
  CardListingView.propTypes = {
    items: PropTypes.arrayOf(PropTypes.any).isRequired,
    compact: PropTypes.bool,
    hide_link: PropTypes.bool,
    grid_layout: PropTypes.bool,
  };

  return CardListingView;
};

export default createCardListingView;
